import {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
  useContext,
  type RefObject,
} from 'react';
import { useIntl } from 'umi';
import { ThemeContext } from '@/pages/home/wrappers/common';
import {
  SYSTEM_THEME_DARK,
  LANGUAGE_CN,
} from '@home/_config/config';
import { doLogout } from '@/utils/utils';

/**
 * 嵌入子 iframe 的父侧 Bridge（GameWiki 等复用）。
 *
 * 职责：
 * - 子应用路由 ↔ 父地址栏 path 同步（F5 / 分享 / 前进后退可恢复深链）
 * - 主题、语言：首屏写入 iframe src query（避免子应用默认暗色 FOUC），运行中靠 postMessage
 * - 子应用 auth-required：主框架顶层执行 doLogout（内嵌场景子页不自行跳登录）
 *
 * 约束：地址栏用 replaceState，不走 Umi history，避免父页面重挂载导致 iframe 重载。
 * theme/lang 只挂在 iframe src，不写进父地址栏。
 */

/** 父框架 themeValue → 子应用 postMessage 协议值 light/dark；无主题时不下发 */
function toChildTheme(themeValue?: string): 'light' | 'dark' | undefined {
  if (!themeValue) return undefined;
  return themeValue === SYSTEM_THEME_DARK ? 'dark' : 'light';
}

/** 父框架 locale → 子应用 postMessage 协议值 zh/en */
function toChildLang(locale: string): 'zh' | 'en' {
  return locale === LANGUAGE_CN ? 'zh' : 'en';
}

// ---------------------------------------------------------------------------
// 父 URL Path 读写
//
// 两套坐标系（只在校验边界转换一次，内部一律用「业务 path」）：
// - 业务 path：Umi 路由配置用的路径，如 /gamewiki/kb/platform/workbench
// - 浏览器 path：地址栏真实路径，含 Umi base，如 /v2/gamewiki/kb/platform/workbench
//
// replaceState 必须写浏览器 path；解析嵌入路由时从 window.location 读入再转业务 path。
// 不走 Umi history.replace，避免父页面路由重匹配导致组件重挂载。
// ---------------------------------------------------------------------------

const DEFAULT_APP_BASE = '/v2';

/** 路由映射上下文，供外部 callback 读取挂载信息与当前父业务 path */
export interface IEmbedRouteMapContext {
  parentMountPath: string;
  basePath: string;
  defaultRoute: string;
  appBase: string;
  /** 映射发生时的父业务 pathname（不含 Umi base） */
  currentBusinessPathname?: string;
}

/**
 * 父地址栏业务 path → 子应用 fullPath。
 * 返回 null 时走默认规则（parentMountPath + basePath + childPath）。
 */
export type TParentToChildPath = (
  businessPathname: string,
  search: string,
  ctx: IEmbedRouteMapContext,
) => string | null;

/**
 * 子应用 fullPath → 父地址栏业务 path（含 search）。
 * 返回 null 时走默认拼接规则。
 */
export type TChildToParentPath = (
  childFullPath: string,
  ctx: IEmbedRouteMapContext,
) => string | null;

interface IEmbedPathContext extends IEmbedRouteMapContext {
  parentToChildPath?: TParentToChildPath;
  childToParentPath?: TChildToParentPath;
}

/** 浏览器 pathname → 业务 pathname（去掉 Umi base /v2） */
function toBusinessPathname(pathname: string, appBase: string): string {
  if (!appBase || pathname === appBase) {
    return pathname === appBase ? '/' : pathname;
  }
  if (pathname.startsWith(`${appBase}/`)) {
    return pathname.slice(appBase.length) || '/';
  }
  // 无 /v2 前缀时按业务 path 处理（兼容本地或非标准部署）
  return pathname;
}
/** 业务 pathname → 浏览器 pathname（补上 Umi base /v2） */
function toBrowserPathname(pathname: string, appBase: string): string {
  if (!appBase) return pathname;
  if (pathname === appBase || pathname.startsWith(`${appBase}/`)) {
    return pathname;
  }
  return `${appBase}${pathname.startsWith('/') ? pathname : `/${pathname}`}`;
}

/** 父页嵌入段前缀：/gamewiki + /kb → /gamewiki/kb */
function getEmbedPrefix(ctx: IEmbedPathContext): string {
  const mount = ctx.parentMountPath.replace(/\/$/, '');
  const base = ctx.basePath.replace(/\/$/, '');
  return `${mount}${base}`;
}

/** 拆分路径与 query，避免白名单校验时误把 ?tenantId= 当作 path 段 */
function splitPathAndSearch(fullPath: string): {
  path: string;
  search: string;
} {
  const i = fullPath.indexOf('?');
  if (i === -1) return { path: fullPath, search: '' };
  return { path: fullPath.slice(0, i), search: fullPath.slice(i) };
}

/** 去掉子路径尾斜杠，避免与映射规则不一致 */
function normalizeChildPathSegment(path: string): string {
  if (path.length > 1 && path.endsWith('/')) {
    return path.replace(/\/+$/, '');
  }
  return path;
}

/**
 * 统一子应用 fullPath：去掉可选 basePath 前缀并规范化 path。
 * 子应用上报可能带 /kb 前缀或尾斜杠，映射前需归一化。
 */
function normalizeChildFullPath(
  childFullPath: string,
  basePath: string,
): string {
  const { path, search } = splitPathAndSearch(childFullPath);
  const base = basePath.replace(/\/$/, '');
  let normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (normalizedPath.startsWith(`${base}/`)) {
    normalizedPath = normalizedPath.slice(base.length) || '/';
  }
  return `${normalizeChildPathSegment(normalizedPath)}${search}`;
}

/** 构建 child→parent 映射用的上下文 */
function buildRouteMapContext(ctx: IEmbedPathContext): IEmbedRouteMapContext {
  const currentBusinessPathname =
    typeof window !== 'undefined'
      ? toBusinessPathname(window.location.pathname, ctx.appBase)
      : undefined;
  return {
    parentMountPath: ctx.parentMountPath,
    basePath: ctx.basePath,
    defaultRoute: ctx.defaultRoute,
    appBase: ctx.appBase,
    currentBusinessPathname,
  };
}

/** 子应用 fullPath → 写入父地址栏的业务 path */
function resolveParentBusinessPath(
  ctx: IEmbedPathContext,
  childFullPath: string,
): string {
  const normalizedChildPath = normalizeChildFullPath(
    childFullPath,
    ctx.basePath,
  );
  if (ctx.childToParentPath) {
    const mapped = ctx.childToParentPath(
      normalizedChildPath,
      buildRouteMapContext(ctx),
    );
    if (mapped !== null) return mapped;
  }
  return buildBusinessEmbedPath(ctx, normalizedChildPath);
}

/** 子应用 fullPath → 业务嵌入 path，如 /gamewiki/kb/platform/workbench */
function buildBusinessEmbedPath(
  ctx: IEmbedPathContext,
  childFullPath: string,
): string {
  const { path, search } = splitPathAndSearch(childFullPath);
  const childSegment = path.startsWith('/') ? path : `/${path}`;
  const base = ctx.basePath.replace(/\/$/, '');
  const mount = ctx.parentMountPath.replace(/\/$/, '');
  return `${mount}${base}${childSegment}${search}`;
}

/**
 * 业务嵌入 path → 子应用 fullPath（不含 /kb）。
 * 不匹配嵌入段时回退 defaultRoute。
 */
function parseChildRoute(
  businessPathname: string,
  search: string,
  ctx: IEmbedPathContext,
): string {
  // 优先走业务自定义映射（如 GameWiki space 格式）
  if (ctx.parentToChildPath) {
    const mapped = ctx.parentToChildPath(
      businessPathname,
      search,
      buildRouteMapContext(ctx),
    );
    if (mapped !== null) return mapped;
  }

  const prefix = getEmbedPrefix(ctx);
  const mount = ctx.parentMountPath.replace(/\/$/, '');

  // 仅挂载页（/gamewiki 或 /gamewiki/kb）→ 子应用默认首页
  if (
    businessPathname === mount ||
    businessPathname === prefix ||
    businessPathname === `${prefix}/`
  ) {
    return ctx.defaultRoute;
  }
  // 非本嵌入段路径（如误访问其它业务页）→ 兜底默认路由
  if (!businessPathname.startsWith(`${prefix}/`)) {
    return ctx.defaultRoute;
  }
  const childPath = businessPathname.slice(prefix.length);
  return search ? `${childPath}${search}` : childPath;
}

/** 从当前地址栏读取子应用 fullPath */
function readChildRouteFromBrowser(ctx: IEmbedPathContext): string {
  if (typeof window === 'undefined') return ctx.defaultRoute;
  const businessPath = toBusinessPathname(
    window.location.pathname,
    ctx.appBase,
  );
  return parseChildRoute(businessPath, window.location.search, ctx);
}

/** replaceState 写入地址栏（自动补 /v2） */
function replaceBrowserUrl(
  businessPathWithSearch: string,
  appBase: string,
): void {
  if (typeof window === 'undefined') return;

  const { path, search } = splitPathAndSearch(businessPathWithSearch);
  const browserPath = toBrowserPathname(path, appBase);
  const url = `${browserPath}${search}${window.location.hash}`;
  window.history.replaceState(window.history.state, '', url);
}

/** 子应用 route-change 时：将子路由写入父地址栏（replaceState，不触发 Umi 路由） */
function writeChildRouteToBrowser(
  ctx: IEmbedPathContext,
  childFullPath: string,
): void {
  replaceBrowserUrl(resolveParentBusinessPath(ctx, childFullPath), ctx.appBase);
}

/** route-clear 时：地址栏回退到父挂载页（如 /v2/gamewiki） */
function clearBrowserEmbedPath(ctx: IEmbedPathContext): void {
  const mount = ctx.parentMountPath.replace(/\/$/, '');
  replaceBrowserUrl(mount, ctx.appBase);
}

/** 通用路径安全校验；false 表示含注入风险，应丢弃 route-change */
export function isSafeEmbedPath(fullPath: string): boolean {
  if (!fullPath?.startsWith('/')) return false;
  if (fullPath.includes('://')) return false;
  if (fullPath.toLowerCase().includes('javascript:')) return false;
  return true;
}

/**
 * 向路径写入 query：已有同名键则覆盖，避免刷新后 theme/lang 重复追加。
 * 用于 iframe 首屏挂 theme/lang，不影响父地址栏。
 */
function appendQueryParams(
  pathWithOptionalSearch: string,
  params: Record<string, string>,
): string {
  const entries = Object.entries(params).filter(([, v]) => !!v);
  if (!entries.length) return pathWithOptionalSearch;
  const { path, search } = splitPathAndSearch(pathWithOptionalSearch);
  const searchParams = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  );
  entries.forEach(([k, v]) => {
    searchParams.set(k, v);
  });
  const qs = searchParams.toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * 拼接 iframe 首屏 src：basePath + 子路由 + 可选 theme/lang。
 * 运行中不更新 src（靠 postMessage 导航 / 切主题），避免 iframe 重载。
 */
function buildEmbedIframeSrc(
  basePath: string,
  route: string,
  appearance?: { theme?: 'light' | 'dark'; lang?: 'zh' | 'en'; },
): string {
  const base = basePath.replace(/\/$/, '');
  const normalizedRoute = route.startsWith('/') ? route : `/${route}`;
  const path = `${base}${normalizedRoute}`;
  if (!appearance) return path;
  const params: Record<string, string> = {};
  if (appearance.theme) params.theme = appearance.theme;
  if (appearance.lang) params.lang = appearance.lang;
  return appendQueryParams(path, params);
}

// ---------------------------------------------------------------------------
// postMessage 协议常量
// ---------------------------------------------------------------------------

/** 父框架 postMessage 协议 source 标识 */
export const PARENT_FRAME_SOURCE = 'databrain-main-frame';

/** 子 iframe postMessage 协议 source 标识，与 appId 一一对应 */
export function childFrameSource(appId: string): string {
  return `${appId}-iframe`;
}

/** 默认同源：iframe 走 /kb 代理时，子应用 origin 与父页一致 */
function getDefaultChildOrigin(): string {
  return typeof window !== 'undefined' ? window.location.origin : '*';
}

export interface IEmbedChildBridgeOptions {
  /** 嵌入子系统标识，与子应用 postMessage 的 appId 对应（如 'gamewiki'） */
  appId: string;
  /** 父页面挂载路径（业务 path，不含 Umi base），如 '/gamewiki' */
  parentMountPath: string;
  /** 子应用部署根路径（iframe src 前缀），无尾斜杠，如 '/kb' */
  basePath: string;
  /** 子应用默认首页路由（不含 basePath），如 '/platform/workbench' */
  defaultRoute?: string;
  /**
   * 子 iframe 的 origin，用于 postMessage 收发校验。
   * 默认 window.location.origin（同源代理模式）；跨域直连子服务时需显式传入子应用 origin。
   */
  childOrigin?: string;
  /** 子 iframe 引用，用于 postMessage 下发 navigate / theme / lang */
  iframeRef?: RefObject<HTMLIFrameElement | null>;
  /**
   * 业务路径白名单校验（仅 path 段，不含 query）。
   * 返回 false 时 route-change 不入地址栏，navigate 不下发。
   */
  pathValidator?: (fullPath: string) => boolean;
  /**
   * 是否同步父框架主题、语言到子应用。
   * true：首屏写入 iframe src query，ready 与切换时再 postMessage；false：不处理外观同步。
   */
  syncParentAppearance?: boolean;
  /** Umi config.base（浏览器地址栏前缀），默认 '/v2' */
  appBase?: string;
  /** 父业务 path → 子应用 fullPath；返回 null 时走默认 mount+base+child 规则 */
  parentToChildPath?: TParentToChildPath;
  /** 子应用 fullPath → 父业务 path；返回 null 时走默认拼接规则 */
  childToParentPath?: TChildToParentPath;
}

export interface IEmbedChildBridgeReturn {
  embedRoute: string;
  iframeSrc: string;
  navigateChild: (fullPath: string, replace?: boolean) => void;
  setChildTheme: (theme: 'light' | 'dark') => void;
  setChildLang: (lang: 'zh' | 'en') => void;
  clearEmbedRoute: () => void;
}

interface IRouteChangePayload {
  fullPath?: string;
}

interface IReadyPayload {
  fullPath?: string;
}

/** 子应用 token 失效 / 主动登出时上报；reason 仅作日志与区分场景 */
interface IAuthRequiredPayload {
  /** logout：用户主动退出；token-expired：接口 401 / 登录态失效 */
  reason?: 'logout' | 'token-expired';
  href?: string;
  ts?: number;
}

interface IChildMessage {
  source?: string;
  appId?: string;
  type?: string;
  payload?: IRouteChangePayload | IReadyPayload | IAuthRequiredPayload;
}

/**
 * 嵌入子 iframe 父侧 Bridge：地址栏持久化子路由 + postMessage 运行时同步。
 */
export function useEmbedChildBridge(
  options: IEmbedChildBridgeOptions,
): IEmbedChildBridgeReturn {
  const {
    appId,
    parentMountPath,
    basePath,
    defaultRoute = '/',
    childOrigin = getDefaultChildOrigin(),
    iframeRef,
    pathValidator,
    syncParentAppearance = true,
    appBase = DEFAULT_APP_BASE,
    parentToChildPath,
    childToParentPath,
  } = options;

  const pathCtx: IEmbedPathContext = {
    parentMountPath,
    basePath,
    defaultRoute,
    appBase,
    parentToChildPath,
    childToParentPath,
  };

  const { themeValue } = useContext(ThemeContext);
  const { locale } = useIntl();
  const childTheme = syncParentAppearance ? toChildTheme(themeValue) : undefined;
  const childLang = syncParentAppearance ? toChildLang(locale) : undefined;

  /** 从当前地址栏解析子应用 fullPath（含 query） */
  const readChildRoute = useCallback(
    (): string => readChildRouteFromBrowser(pathCtx),
    [
      parentMountPath,
      basePath,
      defaultRoute,
      appBase,
      parentToChildPath,
      childToParentPath,
    ],
  );

  // 首屏子路由，供 iframeSrc 计算；运行中子路由变更只写 ref，不 setState
  const initialRoute =
    typeof window !== 'undefined'
      ? readChildRouteFromBrowser(pathCtx)
      : defaultRoute;
  const [embedRoute] = useState<string>(initialRoute);
  /** 子应用当前 fullPath 内存态；route-change 时更新，与 embedRoute state 分离以防 iframe 重载 */
  const embedRouteRef = useRef(initialRoute);
  /**
   * 首屏 theme/lang 冻结进 iframe src，避免子应用默认暗色闪一下。
   * 依赖不含 childTheme/childLang：父侧后续切换只走 postMessage，不改 src。
   */
  const iframeSrc = useMemo(
    () =>
      buildEmbedIframeSrc(
        basePath,
        embedRoute,
        syncParentAppearance
          ? { theme: childTheme, lang: childLang }
          : undefined,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 故意冻结首屏外观，避免切主题重载 iframe
    [basePath, embedRoute, syncParentAppearance],
  );

  /**
   * 路径是否允许同步到地址栏或下发 navigate。
   * false：不安全路径或业务白名单未通过，应丢弃。
   */
  const isAllowedPath = useCallback(
    (fullPath: string): boolean => {
      // 先过通用安全校验，再过业务白名单（如 GameWiki 允许的路径段）
      if (!isSafeEmbedPath(fullPath)) return false;
      const { path } = splitPathAndSearch(fullPath);
      if (pathValidator && !pathValidator(path)) return false;
      return true;
    },
    [pathValidator],
  );
  /** 向子 iframe 发送 postMessage，targetOrigin 由 childOrigin 约束 */
  const postToChild = useCallback(
    (message: Record<string, unknown>): void => {
      iframeRef?.current?.contentWindow?.postMessage(message, childOrigin);
    },
    [iframeRef, childOrigin],
  );

  /** 父页主动驱动子应用跳转（前进/后退校准、ready 深链恢复） */
  const navigateChild = useCallback(
    (fullPath: string, replace = true): void => {
      if (!isAllowedPath(fullPath)) return;
      postToChild({
        source: PARENT_FRAME_SOURCE,
        type: 'navigate',
        payload: { fullPath, replace },
      });
    },
    [isAllowedPath, postToChild],
  );

  /** 向子应用下发主题 */
  const setChildTheme = useCallback(
    (theme: 'light' | 'dark'): void => {
      postToChild({ source: PARENT_FRAME_SOURCE, type: 'theme', value: theme });
    },
    [postToChild],
  );

  /** 向子应用下发语言 */
  const setChildLang = useCallback(
    (lang: 'zh' | 'en'): void => {
      postToChild({ source: PARENT_FRAME_SOURCE, type: 'lang', value: lang });
    },
    [postToChild],
  );

  /** 子应用 route-clear：清空父地址栏嵌入段并重置内存路由 */
  const clearEmbedRoute = useCallback((): void => {
    clearBrowserEmbedPath(pathCtx);
    embedRouteRef.current = defaultRoute;
  }, [parentMountPath, basePath, defaultRoute, appBase]);

  /** 浏览器前进/后退后：以地址栏为准校准子 iframe 路由 */
  const syncRouteFromBrowser = useCallback((): void => {
    const saved = readChildRoute();
    if (!isAllowedPath(saved)) return;
    // 与内存态一致则不下发 navigate，避免多余 postMessage
    if (saved === embedRouteRef.current) return;
    embedRouteRef.current = saved;
    navigateChild(saved, true);
  }, [readChildRoute, isAllowedPath, navigateChild]);
  /** 将父框架当前主题、语言推送给子应用 */
  const syncChildAppearance = useCallback((): void => {
    if (childTheme !== undefined) setChildTheme(childTheme);
    if (childLang !== undefined) setChildLang(childLang);
  }, [childTheme, childLang, setChildTheme, setChildLang]);

  useEffect(() => {
    // 父框架主题/语言变化时推送给子应用
    syncChildAppearance();
  }, [syncChildAppearance]);

  useEffect(() => {
    const expectedSource = childFrameSource(appId);

    /** 校验 origin/source/appId 后分发子应用 route-change / ready / route-clear / auth-required */
    const handleChildMessage = (event: MessageEvent<IChildMessage>): void => {
      if (event.origin !== childOrigin) return;

      const { data } = event;
      if (!data || data.source !== expectedSource) return;
      // appId 可选；有值时必须与当前嵌入应用一致
      if (data.appId && data.appId !== appId) return;

      switch (data.type) {
        // 子应用内导航：只改地址栏 + ref，不触发父组件重渲染
        case 'route-change': {
          const rawFullPath = (data.payload as IRouteChangePayload)?.fullPath;
          if (!rawFullPath) return;
          const fullPath = normalizeChildFullPath(rawFullPath, basePath);
          if (!isAllowedPath(fullPath)) return;
          writeChildRouteToBrowser(pathCtx, fullPath);
          embedRouteRef.current = fullPath;
          break;
        }
        case 'route-clear':
          clearEmbedRoute();
          break;
        // 子应用加载完成：用地址栏深链校准子路由，并下发 theme/lang
        case 'ready': {
          const saved = readChildRoute();
          const current = (data.payload as IReadyPayload)?.fullPath ?? '';
          // 地址栏有深链且与子应用当前页不一致 → 以父 URL 为准
          if (saved && saved !== current) {
            navigateChild(saved, true);
          }
          syncChildAppearance();
          break;
        }
        // 子应用登录态失效或主动登出：内嵌时子页不跳登录，由主框架顶层 doLogout
        case 'auth-required': {
          // 先清嵌入段，避免登出回跳仍落在深链嵌入页
          clearEmbedRoute();
          doLogout();
          break;
        }
        default:
          break;
      }
    };
    window.addEventListener('message', handleChildMessage);
    return () => window.removeEventListener('message', handleChildMessage);
  }, [
    appId,
    appBase,
    basePath,
    childOrigin,
    parentToChildPath,
    clearEmbedRoute,
    defaultRoute,
    isAllowedPath,
    navigateChild,
    parentMountPath,
    childToParentPath,
    readChildRoute,
    syncChildAppearance,
  ]);

  // 仅监听 popstate（前进/后退）；不用 Umi location，避免父路由重匹配
  useEffect(() => {
    /** 地址栏因浏览器导航变化时，同步子 iframe */
    const onPopState = (): void => {
      syncRouteFromBrowser();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [syncRouteFromBrowser]);

  return {
    embedRoute,
    iframeSrc,
    navigateChild,
    setChildTheme,
    setChildLang,
    clearEmbedRoute,
  };
}
